import { Link } from 'react-router-dom';
import { useAppStore } from '../../contexts/store';
import { Mail, Phone, Globe } from 'lucide-react';
import { sanitizeLogoUrl, formatE164ForDisplay } from '../../utils/validation';

export function Footer() {
  const { tenantConfig } = useAppStore();

  if (!tenantConfig) return null;

  const logoUrl = tenantConfig?.logoUrl
    ? (sanitizeLogoUrl(tenantConfig.logoUrl) || '/images/logo.jpg')
    : '/images/logo.jpg';

  return (
    <footer className="bg-surface/80 backdrop-blur-sm border-t border-border mt-auto">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Company Info */}
          <div>
            <img
              src={logoUrl}
              alt={tenantConfig.companyName}
              className="h-10 w-auto mb-4"
              loading="lazy"
            />
            <p className="text-sm text-text-secondary leading-relaxed">
              High-speed fiber internet for your community. Experience the future of connectivity.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg text-text mb-4">Contact Us</h3>
            <div className="space-y-3 text-sm">
              {tenantConfig.supportEmail && (
                <a
                  href={`mailto:${tenantConfig.supportEmail}`}
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors group"
                >
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Mail size={16} className="text-primary" />
                  </div>
                  <span className="font-medium">{tenantConfig.supportEmail}</span>
                </a>
              )}
              {tenantConfig.supportPhone && (
                <a
                  href={`tel:${tenantConfig.supportPhone}`}
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors group"
                >
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Phone size={16} className="text-primary" />
                  </div>
                  <span className="font-medium">{formatE164ForDisplay(tenantConfig.supportPhone)}</span>
                </a>
              )}
              {tenantConfig.websiteUrl && (
                <a
                  href={tenantConfig.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-text-secondary hover:text-primary transition-colors group"
                >
                  <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Globe size={16} className="text-primary" />
                  </div>
                  <span className="font-medium">Visit Website</span>
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg text-text mb-4">Quick Links</h3>
            <div className="space-y-3 text-sm">
              <Link to="/" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Home
              </Link>
              <Link to="/pricing" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Pricing
              </Link>
              <Link to="/tools" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Free Tools
              </Link>
              <Link to="/tools/broadband-label" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Free FCC Label Tool
              </Link>
              <Link to="/tools/pon-calculator" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Free PON Calculator
              </Link>
              <Link to="/tools/fiber-color-code" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Fiber Color Code Lookup
              </Link>
              <Link to="/contact" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Contact
              </Link>
              <Link to="/login" className="block text-text-secondary hover:text-primary transition-colors font-medium">
                Sign In
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 pt-6 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-text-muted">
            © {new Date().getFullYear()} {tenantConfig.companyName}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-text-muted hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-text-muted hover:text-primary transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
